import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  BookOpen, Check, ChevronRight, Clock, GraduationCap, Globe, Home, Info, Layers, Lock,
  PlayCircle, Share2, ShieldCheck, Star, Target, Users, X,
} from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { formatPrice } from "@/lib/format";
import { useStore } from "@/context/StoreContext";
import { useFormation, useBuyFormation } from "@/hooks/useFormations";
import { useAuth } from "@/context/AuthContext";
import { RichTextBlocks, stripRichText } from "@/components/RichText";
import RichText from "@/components/RichText";
import { useSeo } from "@/lib/seo";
import { LessonVideo } from "@/components/ProtectedMedia";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

function BulletList({ items, icon: Icon = Check, tone = "text-emerald-500" }) {
  if (!items?.length) return null;
  return (
    <ul className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
          <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function Stars({ value, size = "h-4 w-4", onSelect }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onSelect}
          onClick={() => onSelect && onSelect(n)}
          data-testid={onSelect ? `star-${n}` : undefined}
          className={onSelect ? "transition-transform hover:scale-110" : "cursor-default"}
        >
          <Star className={`${size} ${n <= Math.round(value) ? "text-amber-400" : "text-slate-200"}`} fill="currentColor" />
        </button>
      ))}
    </div>
  );
}

function ReviewsSection({ formation }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [can, setCan] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get(`/formations/${formation.id}/reviews`).then(({ data }) => setData(data));
  useEffect(() => {
    load();
    if (user) {
      api.get(`/my/formations/${formation.id}/can-review`).then(({ data }) => {
        setCan(data);
        if (data.my_review) { setRating(data.my_review.rating); setComment(data.my_review.comment || ""); }
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formation.id, user]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/my/formations/${formation.id}/reviews`, { rating, comment });
      toast.success(can?.already_reviewed ? "Avis mis à jour" : "Merci pour votre avis !");
      await load();
      setCan((c) => ({ ...c, already_reviewed: true, can_review: false }));
    } catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };

  const items = data?.items || [];
  const total = data?.count || 0;
  const avg = data?.avg || 0;
  const dist = data?.distribution || {};
  const realTotal = data?.real_count || 0;

  return (
    <div className="rounded-2xl border border-line bg-white p-6" data-testid="reviews-section">
      <div className="flex flex-col gap-6 sm:flex-row">
        <div className="shrink-0 text-center sm:w-44">
          <p className="font-display text-5xl font-black text-ink">{avg?.toFixed ? avg.toFixed(1) : avg}</p>
          <Stars value={avg} size="h-5 w-5" />
          <p className="mt-1 text-xs text-slate-500">{total.toLocaleString("fr-FR")} avis</p>
        </div>
        <div className="flex-1 space-y-1.5">
          {[5, 4, 3, 2, 1].map((n) => {
            const c = dist[n] || 0;
            const pct = realTotal ? (c / realTotal) * 100 : 0;
            return (
              <div key={n} className="flex items-center gap-2 text-xs">
                <span className="w-3 text-slate-500">{n}</span>
                <Star className="h-3 w-3 text-amber-400" fill="currentColor" />
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-amber-400" style={{ width: `${pct}%` }} /></div>
                <span className="w-6 text-right text-slate-400">{c}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* form */}
      {user && can?.can_review !== undefined && (
        can.can_review || can.already_reviewed ? (
          <div className="mt-6 rounded-xl border border-line bg-surface p-4" data-testid="review-form">
            <p className="mb-2 text-sm font-bold text-ink">{can.already_reviewed ? "Modifier votre avis" : "Laisser un avis"}</p>
            <Stars value={rating} size="h-6 w-6" onSelect={setRating} />
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              data-testid="review-comment"
              placeholder="Partagez votre expérience sur cette formation..."
              className="mt-3 min-h-[90px] w-full rounded-xl border border-line p-3 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
            <button onClick={submit} disabled={submitting} data-testid="review-submit" className="mt-2 flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />} {can.already_reviewed ? "Mettre à jour" : "Publier mon avis"}
            </button>
          </div>
        ) : (
          <p className="mt-6 rounded-xl bg-brand-soft px-4 py-3 text-sm text-slate-600">Seuls les étudiants inscrits à cette formation peuvent laisser un avis.</p>
        )
      )}

      {/* list */}
      <div className="mt-6 space-y-4">
        {items.length === 0 ? (
          <p className="text-sm text-slate-500">Aucun avis pour l'instant. Soyez le premier à donner votre avis !</p>
        ) : items.map((r) => (
          <div key={r.id} className="border-t border-line pt-4" data-testid={`review-${r.id}`}>
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-light font-bold text-brand">{(r.author_name || "?")[0]}</span>
              <div>
                <p className="text-sm font-bold text-ink">{r.author_name}</p>
                <div className="flex items-center gap-2"><Stars value={r.rating} size="h-3.5 w-3.5" />{r.verified && <span className="text-[10px] font-bold text-emerald-600">✓ Achat vérifié</span>}</div>
              </div>
            </div>
            {r.comment && <p className="mt-2 text-sm text-slate-600">{r.comment}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FormationDetail() {
  const { slug } = useParams();
  const { currency } = useStore();
  const buy = useBuyFormation();
  const { data: f, isLoading } = useFormation(slug);
  const [lightbox, setLightbox] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const gallery = useMemo(() => {
    if (!f) return [];
    return [f.image_url, ...(f.gallery || [])].filter(Boolean);
  }, [f]);

  useSeo({
    title: f ? `${f.title} — Formation ISMO-TECH` : "Formation — ISMO-TECH",
    description: f ? stripRichText(f.short_description) : undefined,
    image: f?.image_url,
    jsonLd: f
      ? {
          "@context": "https://schema.org",
          "@type": "Course",
          name: f.title,
          description: stripRichText(f.short_description),
          provider: { "@type": "Organization", name: "ISMO-TECH", sameAs: "https://ismo.tech" },
          offers: { "@type": "Offer", price: f.price, priceCurrency: "XOF", category: "Paid" },
          ...(f.reviews_count
            ? { aggregateRating: { "@type": "AggregateRating", ratingValue: f.rating, reviewCount: f.reviews_count } }
            : {}),
        }
      : null,
  });

  if (isLoading) {
    return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  }
  if (!f) {
    return (
      <div className="container-it py-24 text-center">
        <p className="font-display text-2xl font-extrabold text-ink">Formation introuvable</p>
        <Link to="/formations" className="mt-4 inline-block rounded-full bg-brand px-6 py-3 text-sm font-bold text-white">Retour aux formations</Link>
      </div>
    );
  }

  const openPreview = async (lesson) => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/formations/${f.id}/preview/${lesson.id}`);
      setPreview(data);
    } catch (e) {
      toast.error(apiError(e, "Aperçu indisponible"));
    } finally {
      setPreviewLoading(false);
    }
  };

  const totalLessons = f.program?.reduce((s, m) => s + m.lessons.length, 0) || f.lessons_count;

  return (
    <div className="bg-surface pb-16">
      {/* breadcrumb */}
      <div className="border-b border-line bg-white">
        <div className="container-it flex items-center gap-1.5 py-3 text-xs text-slate-500">
          <Link to="/" className="flex items-center gap-1 hover:text-brand"><Home className="h-3.5 w-3.5" /> Accueil</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to="/formations" className="hover:text-brand">Formations</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="truncate font-semibold text-ink">{f.title}</span>
        </div>
      </div>

      <div className="container-it grid gap-8 py-8 lg:grid-cols-[1fr_360px]">
        {/* LEFT */}
        <div className="min-w-0">
          {/* media */}
          <div className="overflow-hidden rounded-2xl border border-line bg-black">
            {f.promo_video ? (
              <LessonVideo video={f.promo_video} poster={f.image_url} testId="formation-promo-video" />
            ) : f.image_url ? (
              <img src={fileUrl(f.image_url)} alt={f.title} className="aspect-video w-full object-cover" />
            ) : (
              <div className="flex aspect-video w-full items-center justify-center bg-slate-100 text-slate-300"><BookOpen className="h-12 w-12" /></div>
            )}
          </div>
          {gallery.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
              {gallery.map((g, i) => (
                <button key={i} onClick={() => setLightbox(i)} data-testid={`formation-gallery-${i}`} className="h-16 w-24 shrink-0 overflow-hidden rounded-lg border border-line">
                  <img src={g} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* title */}
          <div className="mt-6">
            <span className="inline-flex rounded-full bg-brand-light px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-brand">{f.category}</span>
            <h1 className="mt-3 font-display text-3xl font-black leading-tight text-ink sm:text-4xl">{f.title}</h1>
            <p className="mt-2 text-base text-slate-600"><RichText text={f.short_description} /></p>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
              {f.reviews_count > 0 && (
                <span className="flex items-center gap-1.5">
                  <Star className="h-4 w-4 text-amber-400" fill="currentColor" />
                  <span className="font-bold text-ink">{f.rating}</span>
                  <span className="text-slate-400">({f.reviews_count} avis)</span>
                </span>
              )}
              {f.students_count > 0 && (
                <span className="flex items-center gap-1.5 text-slate-500"><Users className="h-4 w-4" /> {f.students_count.toLocaleString("fr-FR")}+ apprenants</span>
              )}
            </div>
          </div>

          {/* meta */}
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { icon: Layers, label: "Niveau", value: f.level },
              { icon: BookOpen, label: "Cours", value: `${totalLessons} leçons` },
              { icon: Clock, label: "Durée", value: f.duration || "—" },
              { icon: Globe, label: "Langue", value: f.language },
            ].map((m) => (
              <div key={m.label} className="rounded-xl border border-line bg-white p-3">
                <m.icon className="h-4 w-4 text-brand" />
                <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{m.label}</p>
                <p className="text-sm font-bold text-ink">{m.value}</p>
              </div>
            ))}
          </div>

          {/* tabs */}
          <div className="mt-8">
            <Tabs defaultValue="apropos">
              <TabsList className="flex w-full flex-wrap justify-start gap-1 bg-transparent p-0">
                <TabsTrigger value="apropos" data-testid="tab-apropos" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-white">À propos</TabsTrigger>
                <TabsTrigger value="programme" data-testid="tab-programme" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-white">Programme</TabsTrigger>
                <TabsTrigger value="objectifs" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-white">Objectifs</TabsTrigger>
                <TabsTrigger value="public" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-white">Public cible</TabsTrigger>
                <TabsTrigger value="prerequis" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-white">Prérequis</TabsTrigger>
                <TabsTrigger value="avis" data-testid="tab-avis" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-white">Avis</TabsTrigger>
                <TabsTrigger value="formateur" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-white">Formateur</TabsTrigger>
              </TabsList>

              <TabsContent value="apropos" className="mt-5">
                <div className="rounded-2xl border border-line bg-white p-6">
                  <h2 className="font-display text-lg font-bold text-ink">À propos de cette formation</h2>
                  <div className="mt-3 text-sm leading-relaxed text-slate-600">
                    <RichTextBlocks text={f.description} />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="programme" className="mt-5">
                <div className="rounded-2xl border border-line bg-white p-4 sm:p-6" data-testid="formation-program">
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="font-display text-lg font-bold text-ink">Programme de la formation</h2>
                    <span className="text-xs text-slate-500">{f.program?.length || 0} modules • {totalLessons} cours</span>
                  </div>
                  <Accordion type="multiple" defaultValue={[f.program?.[0]?.id].filter(Boolean)}>
                    {(f.program || []).map((m) => (
                      <AccordionItem key={m.id} value={m.id} className="border-line">
                        <AccordionTrigger className="text-left text-sm font-bold text-ink hover:no-underline">
                          <span className="flex-1">{m.title}</span>
                          <span className="mr-2 text-xs font-medium text-slate-400">{m.lessons.length} leçons</span>
                        </AccordionTrigger>
                        <AccordionContent>
                          <ul className="space-y-1">
                            {m.lessons.map((l) => {
                              const free = l.is_free_preview;
                              return (
                                <li key={l.id} className={`flex items-center gap-3 rounded-lg px-3 py-2.5 ${free ? "cursor-pointer hover:bg-brand-soft" : ""}`}
                                    onClick={() => free && openPreview(l)} data-testid={`lesson-row-${l.id}`}>
                                  {free ? <PlayCircle className="h-4 w-4 shrink-0 text-brand" /> : <Lock className="h-4 w-4 shrink-0 text-slate-300" />}
                                  <span className="flex-1 text-sm text-slate-600">{l.title}</span>
                                  {free && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-600">Aperçu gratuit</span>}
                                  <span className="text-xs text-slate-400">{l.duration}</span>
                                </li>
                              );
                            })}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </TabsContent>

              <TabsContent value="objectifs" className="mt-5">
                <div className="rounded-2xl border border-line bg-white p-6">
                  <h2 className="mb-4 font-display text-lg font-bold text-ink">Ce que vous allez apprendre</h2>
                  <BulletList items={f.objectives} />
                </div>
              </TabsContent>
              <TabsContent value="public" className="mt-5">
                <div className="rounded-2xl border border-line bg-white p-6">
                  <h2 className="mb-4 font-display text-lg font-bold text-ink">Cette formation est destinée à</h2>
                  <BulletList items={f.audience} icon={Users} tone="text-brand" />
                </div>
              </TabsContent>
              <TabsContent value="prerequis" className="mt-5">
                <div className="rounded-2xl border border-line bg-white p-6">
                  <h2 className="mb-4 font-display text-lg font-bold text-ink">Prérequis</h2>
                  <BulletList items={f.prerequisites} icon={Target} tone="text-violet-500" />
                </div>
              </TabsContent>
              <TabsContent value="avis" className="mt-5">
                <ReviewsSection formation={f} />
              </TabsContent>
              <TabsContent value="formateur" className="mt-5">
                <div className="flex items-center gap-4 rounded-2xl border border-line bg-white p-6">
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand text-white"><GraduationCap className="h-7 w-7" /></span>
                  <div>
                    <p className="font-display text-lg font-bold text-ink">{f.instructor_name}</p>
                    <p className="text-sm text-slate-500">{f.instructor_bio}</p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* RIGHT (sticky buy card) */}
        <aside>
          <div className="sticky top-[100px] space-y-4">
            <div className="rounded-2xl border border-line bg-white p-5 shadow-lg">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-display text-3xl font-black text-brand" data-testid="detail-price">{formatPrice(f.price, currency)}</span>
                {f.old_price > f.price && <span className="text-sm text-slate-400 line-through">{formatPrice(f.old_price, currency)}</span>}
                {f.discount_percent > 0 && <span className="rounded-full bg-rose-500 px-2 py-0.5 text-[11px] font-black text-white">-{f.discount_percent}%</span>}
              </div>
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
                <ShieldCheck className="h-4 w-4" /> Accès immédiat après confirmation du paiement
              </div>

              {f.has_access ? (
                <Link to={`/mes-formations/${f.id}`} data-testid="detail-access-btn" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-600 px-4 py-3.5 text-sm font-bold text-white transition-colors hover:bg-emerald-700">
                  <GraduationCap className="h-5 w-5" /> Accéder à la formation
                </Link>
              ) : (
                <button data-testid="detail-buy-btn" onClick={() => buy(f)} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-brand px-4 py-3.5 text-sm font-bold text-white btn-glow transition-[background-color,transform] duration-200 hover:bg-brand-dark active:scale-[0.98]">
                  <PlayCircle className="h-5 w-5" /> Acheter cette formation
                </button>
              )}
              <button
                onClick={() => { navigator.clipboard?.writeText(window.location.href).catch(() => {}); toast.success("Lien copié"); }}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-full border border-line px-4 py-3 text-sm font-bold text-ink transition-colors hover:border-brand hover:text-brand"
              >
                <Share2 className="h-4 w-4" /> Partager
              </button>

              <div className="mt-4 space-y-2 border-t border-line pt-4 text-xs text-slate-500">
                {[
                  { icon: BookOpen, t: `${totalLessons} cours` },
                  { icon: Clock, t: `${f.duration || "Accès complet"}` },
                  { icon: Globe, t: `Langue : ${f.language}` },
                  { icon: ShieldCheck, t: f.certificate_enabled ? "Certificat à la fin" : "Sans certificat" },
                  { icon: GraduationCap, t: f.access_duration === "permanent" ? "Accès à vie" : `Accès ${f.access_duration} jours` },
                ].map((x, i) => (
                  <p key={i} className="flex items-center gap-2"><x.icon className="h-4 w-4 text-brand" /> {x.t}</p>
                ))}
              </div>
            </div>

            {f.objectives?.length > 0 && (
              <div className="rounded-2xl border border-line bg-white p-5">
                <h3 className="mb-3 font-display text-sm font-bold text-ink">Ce que vous allez apprendre</h3>
                <BulletList items={f.objectives.slice(0, 6)} />
              </div>
            )}
            {f.audience?.length > 0 && (
              <div className="rounded-2xl border border-line bg-white p-5">
                <h3 className="mb-3 font-display text-sm font-bold text-ink">Cette formation est destinée à</h3>
                <BulletList items={f.audience.slice(0, 5)} icon={Users} tone="text-brand" />
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* lightbox */}
      <Dialog open={lightbox !== null} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-4xl border-0 bg-transparent p-0 shadow-none" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Galerie de la formation</DialogTitle>
          {lightbox !== null && <img src={gallery[lightbox]} alt="" className="w-full rounded-xl" />}
        </DialogContent>
      </Dialog>

      {/* preview modal */}
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-3xl" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Aperçu du cours</DialogTitle>
          {previewLoading ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
          ) : preview ? (
            <div>
              <div className="mb-3 flex items-center gap-2">
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-600">Aperçu gratuit</span>
                <h3 className="font-display text-lg font-bold text-ink">{preview.title}</h3>
              </div>
              <LessonVideo video={preview.video} testId="preview-video" />
              {preview.text_content && (
                <div className="mt-4 max-h-40 overflow-y-auto text-sm text-slate-600"><RichTextBlocks text={preview.text_content} /></div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
